/**
 * Inbound webhook signing secrets (#360) — one keyring per organization for
 * `/webhooks/entitlement` and `/webhooks/identity`.
 *
 * Shown once, exactly like an API key. The server returns a secret from the
 * generate call and from nowhere else; the list carries only each key's public
 * id and creation time. The secret lives in component state only, so nothing
 * that refreshes the list can bring it back.
 *
 * Generating keeps the previous key valid (two at most), which is how a sender
 * is switched over without a gap: generate, redeploy the sender, then retire
 * the old key. Server-side RBAC (`apikeys:manage`) is the boundary; the gating
 * here only hides controls the caller could not use.
 */
import { useState } from "react";
import { useAsync } from "../useAsync.js";
import { can, type Grant } from "../rbac.js";
import { relativeTime } from "../time.js";
import { api, type WebhookSecretState } from "../api.js";

const EMPTY: WebhookSecretState = { configured: false, keys: [], legacySignatureAccepted: false };

export function WebhookSecrets({ org, grant }: { org: string; grant: Grant | null }) {
  const mayManage = can(grant, "apikeys:manage", org);
  const [rev, setRev] = useState(0);
  const { data, error, loading } = useAsync(
    () => (mayManage && org ? api.webhookSecret(org) : Promise.resolve(EMPTY)),
    [org, rev, mayManage],
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  /** The secret just generated. Unrecoverable once this unmounts or is dismissed. */
  const [issued, setIssued] = useState<{ secret: string; kid: string } | null>(null);
  const [copied, setCopied] = useState(false);

  if (!mayManage) {
    return (
      <p className="muted" style={{ marginBottom: 0 }}>
        Managing webhook signing secrets requires the <code>apikeys:manage</code> capability, which
        your role does not have.
      </p>
    );
  }

  const state = data ?? EMPTY;

  const generate = async () => {
    const replacing = state.keys.length > 0;
    if (
      replacing &&
      !confirm(
        state.keys.length >= 2
          ? "Generate a new secret? The oldest key stops working immediately; the current one keeps working until you retire it."
          : "Generate a new secret? The current one keeps working until you retire it, so you can switch the sender over first.",
      )
    ) {
      return;
    }
    setMsg("");
    setBusy(true);
    try {
      const out = await api.generateWebhookSecret(org);
      setIssued({ secret: out.secret, kid: out.kid });
      setCopied(false);
      setRev((n) => n + 1);
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  const retire = async (kid: string) => {
    if (!confirm(`Retire key ${kid}? Any sender still signing with it gets 401 within a minute.`)) return;
    setMsg("");
    setBusy(true);
    try {
      await api.retireWebhookKey(org, kid);
      setRev((n) => n + 1);
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      {issued && (
        <div className="card" style={{ borderColor: "var(--warn)", background: "var(--warn-soft)" }}>
          <strong>Copy this secret now — it will not be shown again.</strong>
          <p className="muted">
            Give it to the systems that call the entitlement and identity webhooks for this
            organization. They sign <code>{"<timestamp>.<body>"}</code> with it and send the
            timestamp in <code>x-addressium-timestamp</code>. If you lose it, generate a new one and
            retire <code>{issued.kid}</code>.
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              readOnly
              value={issued.secret}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="New webhook secret"
              style={{ flex: 1, minWidth: 280, fontFamily: "monospace" }}
            />
            <button
              className="btn"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(issued.secret)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            <button className="btn ghost" onClick={() => setIssued(null)}>
              I have stored it
            </button>
          </div>
        </div>
      )}

      {error && <p className="err">{String(error)}</p>}
      {!loading && state.keys.length === 0 && (
        <p className="muted">
          No signing secret has been generated for this organization.
          {state.legacySignatureAccepted
            ? " Its webhooks are accepted only through the deployment-wide legacy secret, which is being retired."
            : " Its webhooks are refused until one is."}
        </p>
      )}
      {state.keys.length > 0 && (
        <table>
          <thead>
            <tr><th>Key</th><th>Created</th><th /></tr>
          </thead>
          <tbody>
            {state.keys.map((k, i) => (
              <tr key={k.kid}>
                <td style={{ fontFamily: "monospace" }}>
                  {k.kid}
                  <div className="muted" style={{ fontSize: "0.85em" }}>{i === 0 ? "current" : "previous"}</div>
                </td>
                <td className="muted">{k.createdAt ? relativeTime(k.createdAt) : "—"}</td>
                <td>
                  {state.keys.length > 1 && (
                    <button className="btn ghost" disabled={busy} onClick={() => void retire(k.kid)}>
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
        <button className="btn" disabled={busy || loading} onClick={() => void generate()}>
          {state.keys.length > 0 ? "Generate new secret" : "Generate secret"}
        </button>
        {msg && <span className="err">{msg}</span>}
      </div>
    </div>
  );
}
